import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Wallet } from '@solana/wallet-adapter-react'

import { solanaWallet, wagmiWallet, walletsConfig as wallets } from '@/config'
import { EChainType } from '@/constants/chains'
import { useAllPublicKeys, useAllWalletAddresses } from '@/data'
import { WalletObserverSubject } from '@/hooks/use-observer-callback'
import useSetSentryUser from '@/hooks/wallets/use-set-sentry-user'
import useSolanaWallet, { useReconnectSol } from '@/hooks/wallets/use-sol-wallets'
import useTrackConnectedWallet from '@/hooks/wallets/use-track-connected-wallet'
import useWagmiWallet, { useReconnectEvm } from '@/hooks/wallets/use-wagmi-wallet'
import { EVM_NETWORK_KEY, isMobileDevice, StoredWalletData, WalletStorage } from '@/utils'
import { isChainSupportKit, isEvmChain } from '@/utils/chain'
import { CustomError } from '@/utils/error'
import handleConnectWallet from '@/utils/wallet/connectWallet'
import { IWallet, IWalletConnectOptions, MultiWalletContextType, WalletContextType } from '@/wallets/types'

import { Connector } from 'wagmi'

const MultiWalletContext = createContext<MultiWalletContextType | undefined>(undefined)

interface WalletProviderProps {
  children: ReactNode
}
type WalletsMap = Record<string, WalletContextType>
const CONNECTION_TIMEOUT = 30000
export const MultiWalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [connectedWallets, _setConnectedWallets] = useState<WalletsMap>({})
  const setConnectedWallets = useCallback((wallets: (prev: WalletsMap) => WalletsMap) => {
    _setConnectedWallets((prev: WalletsMap) => {
      const replaceWallets = wallets(prev)
      const nonEvm = Object.entries(replaceWallets).filter(([network]) => !isEvmChain(network))
      const evm = Object.entries(replaceWallets).find(([_, data]) => data?.networkInfo?.networkType === EChainType.EVM)

      const newWallets = { ...Object.fromEntries(nonEvm) }
      if (evm) newWallets[EVM_NETWORK_KEY] = evm[1] // merge all evm to one
      return newWallets
    })
  }, [])

  const walletsProxy = useMemo(() => {
    const proxy = new Proxy(connectedWallets, {
      get(target, prop: string) {
        if (isEvmChain(prop)) {
          return target[EVM_NETWORK_KEY]
        }
        return target[prop]
      },
    })
    return proxy
  }, [connectedWallets])

  const [isInitializing, setIsInitializing] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const connectionTimeoutRef = useRef<NodeJS.Timeout>()

  const { data: walletPubkeys = [] } = useAllPublicKeys(connectedWallets)
  const connectedWalletsRef = useRef(connectedWallets)

  useEffect(() => {
    if (isInitializing || isConnecting) {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current)
      }

      connectionTimeoutRef.current = setTimeout(() => {
        handleCancelWallet()
      }, CONNECTION_TIMEOUT)

      return () => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current)
          connectionTimeoutRef.current = undefined
        }
      }
    }
  }, [isInitializing, isConnecting])

  useEffect(() => {
    connectedWalletsRef.current = connectedWallets
  }, [connectedWallets])

  const walletAddresses = useMemo(
    () =>
      Object.values(connectedWallets || {})
        .map((wallet) => wallet?.address ?? '')
        .filter(Boolean),
    [connectedWallets]
  )

  useWagmiWallet()
  useSolanaWallet()
  useSetSentryUser({ walletAddresses })
  useTrackConnectedWallet(connectedWallets)

  const onChangeAccount = async (network: string, data: WalletContextType) => {
    console.log('🚀 ~ onChangeAccount ~ network:', { network, data })
    WalletObserverSubject.notify(data)
    if (isChainSupportKit(network)) {
      setConnectedWallets((prev) => ({
        ...prev,
        [network]: data,
      }))
      return
    }
    const networksToUpdate: Array<{ network: string; promise: Promise<string> }> = []

    if (!isMobileDevice() && data.wallet) {
      const walletType = data.wallet.key

      for (const otherNetwork of Object.keys(connectedWalletsRef.current)) {
        if (connectedWalletsRef.current[otherNetwork]?.wallet?.key === walletType) {
          if (wallets[otherNetwork]?.[walletType]) {
            networksToUpdate.push({
              network: otherNetwork,
              promise: wallets[otherNetwork][walletType].getAddress(otherNetwork),
            })
          }
        }
      }
    }

    setConnectedWallets((prev) => ({
      ...prev,
      [network]: data,
    }))

    if (networksToUpdate.length > 0) {
      try {
        const results = await Promise.all(
          networksToUpdate.map(async (item) => {
            try {
              const address = await item.promise
              return { network: item.network, address }
            } catch (error) {
              console.error(`Failed to get address for ${item.network}:`, error)
              return null
            }
          })
        )

        setConnectedWallets((prev) => {
          const newState = { ...prev }

          results.forEach((result) => {
            if (result) {
              newState[result.network] = {
                ...prev[result.network],
                address: result.address,
              }
            }
          })

          return newState
        })
      } catch (error) {
        console.debug('Failed to update addresses for other networks:', error)
      }
    }
  }

  const connectedWalletsCount = Object.keys(connectedWallets).length

  const { data } = useAllWalletAddresses(connectedWallets)
  useEffect(() => {
    if (!data?.length) return
    setConnectedWallets((prev) => {
      return data.reduce(
        (acc, { network, address }) => {
          acc[network] = { ...acc[network], address }
          return acc
        },
        { ...prev }
      )
    })
  }, [data])

  useEffect(() => {
    if (connectedWalletsCount) {
      WalletStorage.saveWallets(connectedWallets)
    }
  }, [connectedWallets, connectedWalletsCount])

  const handleReconnect = ({
    adapter,
    network,
    data,
    selectedWallet,
  }: {
    adapter: Connector | Wallet
    network: string
    data: StoredWalletData
    selectedWallet: IWallet
  }) => {
    handleConnectWallet({
      selectedWallet,
      options: { network: data.networkInfo, adapter, reconnect: true },
      network,
      disconnect: () => disconnectWallet(network),
      onChangeAccount: (data) => onChangeAccount(network, data),
    })
  }

  const isReconnectingEvm = useReconnectEvm((data) => handleReconnect({ ...data, selectedWallet: wagmiWallet }))
  const isReconnectingSol = useReconnectSol((data) => handleReconnect({ ...data, selectedWallet: solanaWallet }))

  const reconnected = useRef(false)
  // reconnect btc
  useEffect(() => {
    if (reconnected.current) return
    reconnected.current = true
    const loadSavedWallets = async () => {
      setIsInitializing(true)
      try {
        const parsed = WalletStorage.getSaveWallets()
        if (!parsed) return
        const connectedNetworks: Record<string, WalletContextType> = {}

        for (const [network, data] of Object.entries(parsed)) {
          if (isChainSupportKit(network)) continue // evm,sol we already handle above
          if (wallets[network]?.[data.walletType]) {
            try {
              const newState = await handleConnectWallet({
                selectedWallet: wallets[network][data.walletType],
                options: { network: data.networkInfo, reconnect: true },
                network,
                disconnect: async () => await disconnectWallet(network),
                onChangeAccount: (data) => onChangeAccount(network, data),
              })

              connectedNetworks[network] = newState
            } catch (error) {
              console.error(`Failed to reconnect ${data.walletType} on ${network}:`, error)
              WalletStorage.removeNetwork(network)
            }
          } else {
            WalletStorage.removeNetwork(network)
          }
        }

        if (Object.keys(connectedNetworks).length) {
          setConnectedWallets((prev) => {
            return { ...prev, ...connectedNetworks }
          })
        }
      } catch (error) {
        console.error('Error loading saved wallets:', error)
        WalletStorage.clearSavedWallets()
      } finally {
        setIsInitializing(false)
      }
    }

    loadSavedWallets()
  }, [])

  const connectWallet = async (selectedWallet: IWallet, network: string, options: IWalletConnectOptions) => {
    if (!selectedWallet) {
      throw new CustomError(`Network ${network} is not supported`)
    }

    setIsConnecting(true)

    try {
      await handleConnectWallet({
        selectedWallet,
        options,
        network,
        disconnect: async () => await disconnectWallet(network),
        onChangeAccount: (data) => onChangeAccount(network, data),
      })
    } catch (error) {
      console.error(`Failed to connect to ${selectedWallet.key} on ${network}:`, error)
      throw error
    } finally {
      setIsConnecting(false)
    }
  }

  const disconnectWallet = async (networkId: string, isDisconnectAll?: boolean) => {
    if (isDisconnectAll) {
      for (const networkId in connectedWallets) {
        try {
          const wallet = connectedWallets[networkId]?.wallet
          await wallet?.disconnect?.(networkId)
        } catch {}
      }
      setConnectedWallets(() => ({}))
      WalletStorage.clearSavedWallets()
      return
    }

    try {
      const wallet = connectedWallets[networkId]?.wallet
      await wallet?.disconnect?.(networkId)
    } catch (error) {
      console.error(`Error disconnecting wallet for ${networkId}:`, error)
    }

    setConnectedWallets((prev) => {
      const newWallets = { ...prev }
      delete newWallets[networkId]
      return newWallets
    })

    WalletStorage.removeNetwork(networkId)
  }

  const handleCancelWallet = () => {
    setIsInitializing(false)
    setIsConnecting(false)

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current)
      connectionTimeoutRef.current = undefined
    }
  }

  const contextValue: MultiWalletContextType = {
    wallets: walletsProxy,
    walletAddresses,
    walletPubkeys,
    connectWallet,
    disconnectWallet,
    handleCancelWallet,
    isInitializing: isInitializing || isReconnectingEvm || isReconnectingSol,
    isConnecting,
    isConnected: walletAddresses?.length > 0,
  }

  return <MultiWalletContext.Provider value={contextValue}>{children}</MultiWalletContext.Provider>
}

export const useMultiWallet = () => {
  const context = useContext(MultiWalletContext)
  if (!context) {
    throw new Error('useMultiWallet must be used within a MultiWalletProvider')
  }
  return context
}
